/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Id } from 'react-toastify';
import AlertToastIcon from './AlertToastIcon';
import type { RetainedToast } from './toastHistoryStore';

interface Props {
  toasts: ReadonlyArray<RetainedToast>;
  onOpen: (id: Id) => void;
  onDismiss: (id: Id) => void;
  onClear: () => void;
}

/** "Recent alerts" panel above the alerts tray pill: one line per retained warning/error. */
export default function AlertHistoryPanel({ toasts, onOpen, onDismiss, onClear }: Readonly<Props>) {
  return (
    <Paper
      aria-label="Warning and error history"
      id="alert-history"
      role="region"
      elevation={0}
      sx={{
        width: 380,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: '14px',
        border: '1px solid var(--pa-toast-border)',
        bgcolor: 'var(--pa-toast-surface)',
        boxShadow: 'var(--pa-toast-shadow)',
        backdropFilter: 'blur(24px) saturate(160%)',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pl: 1.75, pr: 1, py: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Recent alerts</Typography>
        <Typography sx={{ flex: 1, fontSize: 12, color: 'text.secondary' }}>
          {toasts.length}
        </Typography>
        <Button
          size="small"
          color="inherit"
          onClick={onClear}
          sx={{ fontSize: 12, textTransform: 'none', color: 'text.secondary' }}
        >
          Clear all
        </Button>
      </Stack>
      <Divider />
      <Stack spacing={0.25} sx={{ p: 0.75, maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
        {toasts.map(({ id, type, summary }) => (
          <Stack key={id} direction="row" sx={{ alignItems: 'center', borderRadius: '8px' }}>
            <ButtonBase
              aria-label={`View ${type}: ${summary}`}
              onClick={() => onOpen(id)}
              sx={{
                flex: 1,
                minWidth: 0,
                gap: 1.25,
                justifyContent: 'flex-start',
                px: 1,
                py: 0.75,
                borderRadius: '8px',
                textAlign: 'left',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <AlertToastIcon type={type} />
              <Typography noWrap sx={{ flex: 1, fontSize: 12.5, fontWeight: 500 }}>
                {summary}
              </Typography>
              <ChevronRightRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </ButtonBase>
            <Tooltip title={`Dismiss ${type}`}>
              <IconButton
                aria-label={`Dismiss ${type}: ${summary}`}
                onClick={() => onDismiss(id)}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <CloseRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ px: 1.75, pt: 0.25, pb: 1.25 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          Errors and warnings stay here until you leave the page.
        </Typography>
      </Box>
    </Paper>
  );
}
